/**
 * Client-side navigation-sidebar preference library.
 *
 * Persists whether the navigation sidebar of `<fm-application-frame>` is
 * collapsed to its icon rail, under `fm_sidebar` with `'open'` or
 * `'collapsed'`; open is the default. Per device and per user, with no
 * cross-tab listener and no boot script, which `docs/preferences.md` →
 * *Navigation width* owns.
 */

import { getUserId } from './auth.js';
import {
    readUserScopedEntry,
    sidebarPreferenceKey,
    writeUserScopedEntry,
} from './local_storage.js';
import { isEnumMember } from './type_guards/values.js';

type SidebarState = 'open' | 'collapsed';

const allowedStates: ReadonlyArray<SidebarState> = [
    'open',
    'collapsed',
];

/**
 * Checks whether the given value is one of the allowed sidebar states.
 * @param value - The value to test.
 * @returns Whether the value is a valid `SidebarState`.
 */
function isValidState (value: unknown): value is SidebarState {
    return isEnumMember(value, allowedStates);
}

/**
 * Reads the stored state and reports whether the sidebar starts collapsed.
 *
 * Nobody signed in reads the default: the rail is only ever drawn behind an
 * authentication, so there is no width to remember before one.
 * @returns Whether the sidebar is collapsed; `false` when nothing valid is stored.
 */
export function readSidebarCollapsed (): boolean {
    const userId = getUserId();

    if (userId === null) {
        return false;
    }

    return readUserScopedEntry(sidebarPreferenceKey, userId, isValidState, 'open') === 'collapsed';
}

/**
 * Stores the sidebar state for the signed-in user, and for nobody else.
 * @param collapsed - Whether the sidebar is now collapsed.
 */
export function writeSidebarCollapsed (collapsed: boolean): void {
    const userId = getUserId();

    if (userId === null) {
        return;
    }

    writeUserScopedEntry(sidebarPreferenceKey, userId, collapsed ? 'collapsed' : 'open');
}
